/**
 * No Cart Redirect - Prevents redirect to cart page after adding product
 * This script overrides the default cart behavior to keep users on the product page
 */

(function() {
  'use strict';
  
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNoCartRedirect);
  } else {
    initNoCartRedirect();
  }
  
  function initNoCartRedirect() {
    // Override the cart-items element behavior
    const originalAddToCart = window.CartItems?.prototype?.addToCart;
    
    if (window.CartItems && originalAddToCart) {
      window.CartItems.prototype.addToCart = function(formData, button) {
        // Call original method with modified behavior
        const originalLocation = window.location.href;
        
        // Temporarily override window.location to prevent redirect
        const originalLocationSetter = Object.getOwnPropertyDescriptor(window, 'location');
        Object.defineProperty(window, 'location', {
          set: function() {
            // Do nothing - prevent redirect
            console.log('Cart redirect prevented - staying on product page');
          },
          get: function() {
            return originalLocationSetter.get.call(window);
          },
          configurable: true
        });
        
        // Call original add to cart
        const result = originalAddToCart.call(this, formData, button);
        
        // Restore original location setter after a delay
        setTimeout(() => {
          Object.defineProperty(window, 'location', originalLocationSetter);
        }, 100);
        
        return result;
      };
    }
    
    // Alternative approach: Listen for cart add events and prevent default behavior
    document.addEventListener('theme:product:add', function(event) {
      // Show success message instead of redirecting
      showAddToCartSuccess();
    });
    
    // Monitor for cart-items element if not already present
    const observer = new MutationObserver(function(mutations) {
      const cartItems = document.querySelector('cart-items');
      if (cartItems && cartItems.addToCart) {
        overrideCartBehavior(cartItems);
        observer.disconnect();
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Check if cart-items already exists
    const existingCartItems = document.querySelector('cart-items');
    if (existingCartItems) {
      overrideCartBehavior(existingCartItems);
    }
  }
  
  function overrideCartBehavior(cartItems) {
    // Store original addToCart method
    const originalAddToCart = cartItems.addToCart;
    
    // Override addToCart method
    cartItems.addToCart = function(formData, button) {
      // Store original settings
      const originalCartType = theme.settings.cartType;
      
      // Temporarily set cart type to drawer to prevent page redirect
      theme.settings.cartType = 'drawer';
      
      // Create custom fetch request
      fetch(theme.routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript, application/json'
        },
        body: formData
      })
      .then(response => response.json())
      .then(data => {
        // Dispatch success event
        document.dispatchEvent(new CustomEvent('theme:product:add', {
          detail: {
            product: data,
            button: button
          },
          bubbles: true
        }));
        
        // Update cart count if element exists
        updateCartCount();
        
        // Show success message
        showAddToCartSuccess();
        
        // Re-enable button
        if (button) {
          button.classList.remove('is-loading');
          button.removeAttribute('disabled');
        }
        
        // Update cart drawer if it exists
        if (document.querySelector('cart-drawer')) {
          cartItems.getCart();
        }
      })
      .catch(error => {
        console.error('Add to cart error:', error);
        
        // Re-enable button on error
        if (button) {
          button.classList.remove('is-loading');
          button.removeAttribute('disabled');
        }
        
        // Dispatch error event
        document.dispatchEvent(new CustomEvent('theme:product:add-error', {
          detail: {
            error: error,
            button: button
          },
          bubbles: true
        }));
      })
      .finally(() => {
        // Restore original cart type
        theme.settings.cartType = originalCartType;
      });
    };
  }
  
  function updateCartCount() {
    // Fetch current cart to update count
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Update cart count in header
        const cartCountElements = document.querySelectorAll('[data-cart-count]');
        cartCountElements.forEach(element => {
          element.textContent = cart.item_count;
        });
        
        // Update cart total if elements exist
        const cartTotalElements = document.querySelectorAll('[data-cart-total]');
        cartTotalElements.forEach(element => {
          element.textContent = formatMoney(cart.total_price);
        });
      });
  }
  
  function showAddToCartSuccess() {
    // Create success notification
    const notification = document.createElement('div');
    notification.className = 'cart-notification';
    notification.innerHTML = `
      <div class="cart-notification__content">
        <svg class="cart-notification__icon" width="20" height="20" viewBox="0 0 20 20">
          <path fill="currentColor" d="M10 0C4.5 0 0 4.5 0 10s4.5 10 10 10 10-4.5 10-10S15.5 0 10 0zm-1 15l-5-5 1.4-1.4 3.6 3.6 7.6-7.6L18 6l-9 9z"/>
        </svg>
        <span class="cart-notification__text">Prodotto aggiunto al carrello!</span>
      </div>
    `;
    
    // Add styles if not already present
    if (!document.querySelector('#cart-notification-styles')) {
      const styles = document.createElement('style');
      styles.id = 'cart-notification-styles';
      styles.textContent = `
        .cart-notification {
          position: fixed;
          top: 100px;
          right: 20px;
          background: #4caf50;
          color: white;
          padding: 15px 20px;
          border-radius: 4px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.2);
          z-index: 9999;
          animation: slideIn 0.3s ease-out;
        }
        
        .cart-notification__content {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .cart-notification__icon {
          flex-shrink: 0;
        }
        
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(styles);
    }
    
    // Add notification to page
    document.body.appendChild(notification);
    
    // Remove notification after 3 seconds
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 3000);
  }
  
  function formatMoney(cents) {
    // Format price based on Shopify money format
    const money = (cents / 100).toFixed(2);
    return window.theme?.formatMoney ? 
      window.theme.formatMoney(cents, window.theme.settings?.moneyFormat || '€{{amount}}') : 
      '€' + money;
  }
})();